import { Injectable } from '@angular/core';
import { forEach, get, isEmpty, join, template as templateL, trim, uniq } from 'lodash';
import { QueryParamsResolver } from './query.params.resolver';
import { Query } from './query';
import { KeywordToResourceResolver } from './keyword.to.resource.resolver';
import { StrictSqlWhereParserService } from '../query.expression.parser/strict.sql.where.parser.service';
import {
  GroupChipExpression,
  SimpleChipExpression,
  jsonFormattedFilter
} from './common.data.types';
import { TimeoutWrapper } from '@nrwl/common-lib';
import { QueryStatistics } from './query.statistics';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const warnMessage =
  'The chosen time range is greater than 1 day. Certain items, like IP or MAC address, DNS name or user ID, may not display. Please choose a time range 1 day or less to see these items.';

@Injectable()
export class QueryBuilderService {
  constructor(
    private readonly queryEngine: StrictSqlWhereParserService,
    private readonly keywordToResource: KeywordToResourceResolver
  ) {}

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  buildQuery(params: any, def: any, resolver: QueryParamsResolver, queryParams?: any): Query {
    const template = templateL(JSON.stringify(def), {
      imports: {
        groupBy(...args) {
          return groupByHandler(resolver, args);
        },
        KPI(KPI, directionalKPI) {
          //Don't use with static aliases,
          //if you use static aliases, just use static kpiName (directionalKPI)
          const groupByInfo = resolver.resolve('groupby');
          if (groupByInfo && !isEmpty(groupByInfo.aliases)) {
            return directionalKPI;
          }
          return KPI;
        }
      }
    });
    try {
      const paramValues = {};
      let filter = null;
      const filterParams = queryParams?.find(item => item.id === 'filter');
      forEach(params, p => {
        const value = resolver.resolve(p);
        // very important: these values shall be used with simple `${param}` expression - which does not
        // know how to deal with arrays. they are converted to [value, value, value, ...] array - and that
        // can break the query syntax. Therefore we are trying to preprocess arrays by pulling 0th value out
        // of it. If more values are present in array - that's a bug and we should cry foul.

        if (p === 'filter' && filterParams?.format === 'json') {
          filter = resolver.resolve(p);
        }
        paramValues[p] =
          p === 'filter' && filterParams?.format === 'json'
            ? ''
            : this.escapeSpecialJsonCharacters(this.extractFromArray(p, value));
      });
      const fullQuery = template(paramValues);

      // final line of defence: _src/_dst style columns should be converted to _resourceid based columns
      const queryStruct = this.convertToJson(fullQuery, [
        'prefilter',
        'postFilter',
        'directionalFilter'
      ]);
      // fieldName - a field name from orderBy array.
      if (filterParams?.format === 'json') {
        jsonFormattedFilter(filter, queryStruct);
      }
      const queryStats = new QueryStatistics();
      this.substituteFieldNames(queryStruct, ['groupby', 'fieldName'], queryStats);
      this.substituteFieldNamesAndValues(
        queryStruct,
        ['prefilter', 'postFilter', 'filter', 'directionalFilter', 'drilldownFilter'],
        queryStats
      );
      this.evaluateTimeRangeAndQueryDestination(queryStruct, queryStats);
      this.renameKpis(queryStruct, queryStats);

      /* turning off the logic that was preventing query from happening.
      if (queryStats.willResultInAnError()) {
        this.notifications.warnWithClose(warnMessage);
        return null;
      }
      */

      return new Query(queryStruct, resolver.getUUID());
    } catch (e) {
      console.error('Error in converting query template to valid query.', e);
      return null;
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  evaluateTimeRangeAndQueryDestination(
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    queryStruct: any,
    queryStats: QueryStatistics
  ) {
    queryStats.queryFilter = queryStruct.queryFilter;
    queryStats.requestFor = queryStruct.options ? queryStruct.options.requestFor : undefined;
    queryStats.timeRange.fromTime = queryStruct.fromTime;
    queryStats.timeRange.toTime = queryStruct.toTime;
    queryStats.timeRange.timerange = queryStruct.timerange;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  extractFromArray(id: any, input: any): any {
    if (!input) {
      return;
    }
    if (Array.isArray(input)) {
      if (input.length > 1 && id !== 'filter') {
        throw new Error('Parameter ' + id + ' resolves to array with more than one value!');
      }
      if (id === 'filter') {
        return input;
      }
      if (input.length > 0 && id !== 'filter') {
        return input[0];
      }
      return '';
    }
    return input;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  escapeSpecialJsonCharacters(input: any): any {
    if (!input) {
      return input;
    }
    const output = JSON.stringify(input);
    // strip leading and ending ""
    return output.replace(/^"(.*)"$/, '$1');
  }

  // helps to determine correctness of complex filter expressions before they are sent to backend
  isValid(q: Query): boolean {
    if (!q) {
      return false;
    }
    // for the moment, lets check validity of filterby condition
    let valid = true;
    this.deepSearch(q.data, 'postFilter', (object, key) => {
      let filter = object[key];
      // skip checking empty filter expressions
      if (!filter) {
        return;
      }
      filter = this.escapeSpecialJsonCharacters(filter);
      valid = valid && this.queryEngine.isExpressionValid(filter);
    });
    return valid;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any,@typescript-eslint/ban-types
  deepSearch(object: any, key: any, callback: Function): any {
    if (!object) {
      return;
    }
    // eslint-disable-next-line no-prototype-builtins
    if (object.hasOwnProperty(key)) {
      callback(object, key);
    }

    for (let i = 0; i < Object.keys(object).length; i++) {
      if (typeof object[Object.keys(object)[i]] === 'object') {
        this.deepSearch(object[Object.keys(object)[i]], key, callback);
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any,@typescript-eslint/no-unused-vars
  convertToJson(jsonAsStr: string, inFields: any[]): any {
    const result = JSON.parse(jsonAsStr);
    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  renameKpis(queryObj: any, queryStats: QueryStatistics): void {
    const queries = get(queryObj.options, 'queries', []);
    for (const querySpecs of queries) {
      const kpiName = querySpecs.kpiName;
      const groupBy = querySpecs.groupby;
      const newKpiName = this.keywordToResource.translateKpiName(
        kpiName,
        this.listFilterFields(groupBy, [])
      );
      querySpecs.kpiName = newKpiName;
      queryStats.registerKpiName(newKpiName);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  substituteFieldNames(queryObj: any, inFields: any[], queryStats: QueryStatistics): void {
    const queries = get(queryObj.options, 'queries', []);
    for (const querySpecs of queries) {
      const kpiName = querySpecs.kpiName;
      for (const fieldName of inFields) {
        this.deepSearch(queryObj.options, fieldName, (object, key) => {
          const fieldValue = object[key];
          // skip checking empty strings
          if (!fieldValue) {
            return;
          }
          const translatedKeywords = this.keywordToResource.translateKeywords(
            kpiName,
            fieldValue.split(',')
          );
          object[key] = translatedKeywords.join();
          queryStats.registerKeywords(translatedKeywords);
        });
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  substituteFieldNamesAndValues(
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    queryObj: any,
    inFields: string[],
    queryStats: QueryStatistics
  ): void {
    // queries are stored in root->options.queries[]
    // for each query, we want to update indicated fields with query expressions
    // and insert new field that would comma-list field names, used in query expressions.
    const queries = get(queryObj.options, 'queries', []);
    for (const querySpecs of queries) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fieldNamesMap = new Map<string, any>();
      this.substituteNamesAndValuesForOneQuery(querySpecs, inFields, fieldNamesMap, queryStats);
      // this quirk demanded by Raju/backend - he could not trust his code to prepend the property.
      if (querySpecs.queryMode === 'L2cache') {
        querySpecs.preGroups = querySpecs.groupby;
      }
      // special check for cases, when backend does not accept empty queryMode - it must be either populated with
      // predefined string, or not sent at all. This case happens when the same query is being populated via mustache
      // parameters.
      if (querySpecs.queryMode === '') {
        delete querySpecs['queryMode'];
      }
    }

    if (queries.length === 0 && queryObj.options && queryObj.options.query) {
      this.substituteNamesAndValuesForOneQuery(
        queryObj,
        inFields,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        new Map<string, any>(),
        queryStats
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  substituteNamesAndValuesForOneQuery(
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    querySpecs: any,
    inFields: string[],
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    fieldNamesMap: any,
    queryStats: QueryStatistics
  ): void {
    const kpiName = querySpecs.kpiName;
    for (const fieldName of inFields) {
      const fieldList = [];
      fieldNamesMap.set(fieldName, fieldList);
      this.deepSearch(querySpecs, fieldName, (object, key) => {
        let fieldValue = object[key];
        // skip checking empty strings
        if (!fieldValue) {
          return;
        }
        // before parsing as chips, we need to escape string content
        fieldValue = this.escapeSpecialJsonCharacters(fieldValue);
        // parse back to chip expression array
        const expressionAsChips = this.queryEngine.parseIntoTokens(fieldValue);
        const convertedChips = this.keywordToResource.translateExpressions(
          kpiName,
          expressionAsChips
        );
        for (const chip of convertedChips) {
          if (chip.isGroup) {
            (chip as GroupChipExpression).group.forEach(member => {
              fieldList.push(member.keyword);
            });
          } else {
            fieldList.push((chip as SimpleChipExpression).keyword);
          }
        }
        queryStats.registerKeywords(fieldList);
        const cleanedChips = this.queryEngine.removePlaceholders(convertedChips);

        if (fieldValue.search('%') != -1) {
          object[key] = fieldValue;
        } else {
          object[key] = this.queryEngine.toExpression(cleanedChips);
        }
      });
    }
  }

  /**
   * Eliminates duplicates. Preserves case (as I believe backend is sensitive on this)
   * @param groupby
   * @param filterFields
   */
  listFilterFields(groupby: string, filterFields: string[]): string {
    const result = {};
    let groupFields = [];
    if (groupby) {
      groupFields = groupby.split(',');
    }
    for (let field of groupFields) {
      field = trim(field);
      if (field === '1') {
        continue;
      }
      result[field] = true;
    }
    if (filterFields) {
      for (let field of filterFields) {
        field = trim(field);
        // parsing filter expressions might result in field name '1'. skip them.
        if (field === '1') {
          continue;
        }
        result[field] = true;
      }
    }
    return Object.keys(result).sort().join();
  }
}

/**
 * This is an attempt to workaround the ugly constraint from nitrodb query plugin:
 * it requires that all fields, that we want to use in 'filter', must be included in 'groupby'.
 *
 * The function assumes given resolver object is capable to resolve magic parameter 'groupby' to some string.
 * That string was built by some other code to contain comma separated list of field names, taken  from 'filter' condition.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
export function groupByHandler(resolver: any, ...args: any): any {
  let result = '';
  if (args) {
    result = join(args, ',');
    if (result.slice(-1).indexOf(',') > -1) {
      result = result.slice(0, -1);
    }
  }
  // object structure: {expression: 'field1,field2,field3', aliases: {...}}
  const dynamicArgs = resolver.resolve('groupby');
  if (dynamicArgs && dynamicArgs.expression && dynamicArgs.expression.length > 0) {
    if (result.length > 0) {
      result += ',';
    }
    result += dynamicArgs.expression;
  }
  // now split on , and keep uniq only
  const allArgs = result.split(',');
  if (allArgs && allArgs.length > 0) {
    allArgs.forEach((item, index, array) => {
      array[index] = trim(item);
    });
    result = join(uniq(allArgs), ',');
  }

  return result;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
export function prepareFakeResponse(
  timeout: TimeoutWrapper,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  query: any,
  // eslint-disable-next-line @typescript-eslint/ban-types
  callback: Function,
  makeError: boolean = false
) {
  const fake = {
    status: makeError ? 'error' : 'success',
    errorCode: makeError ? -1 : null,
    queries: [
      {
        options: {},
        results: []
      }
    ]
  };
  timeout.add(() => callback(fake), 1000);
}
